/* -------------------------------------------------------------------------- */
/*  TheTargetAudience.tsx                                                     */
/* -------------------------------------------------------------------------- */

import React from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ArrowForward } from "@mui/icons-material";

const TheTargetAudience = () => {
  const audiences = [
    {
      title: "Larger Churches",
      description:
        "Churches running multiple evangelistic ministries, events and follow-up processes across staff and volunteer teams.",
      summary:
        "Give evangelism leaders one place to see what is happening across the whole ministry.",
    },
    {
      title: "Complex Evangelism Ministries",
      description:
        "Churches where outreach spans events, courses, conversations, community engagement, door knocking and ongoing follow-up.",
      summary:
        "Connect separate activities into intentional pathways instead of managing each ministry in isolation.",
    },
    {
      title: "Teams Reaching Hundreds of People",
      description:
        "Churches meeting more people than any one evangelism leader can personally know or follow.",
      summary:
        "Create a shared ministry memory so important relationships and next steps do not depend on one person's memory.",
    },
  ];

  return (
    <Box
      component="section"
      id="target-audience"
      sx={{
        width: "100%",
        py: { xs: 10, md: 14 },
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.7,
          background:
            "radial-gradient(900px 420px at 18% 20%, rgba(77, 222, 128, 0.35), transparent 60%), radial-gradient(800px 360px at 82% 30%, rgba(77, 222, 128, 0.35), transparent 60%)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 9 } }}>
          <Typography
            variant="h3"
            sx={{
              color: "white",
              fontWeight: 900,
              letterSpacing: -0.7,
              mb: 2,
            }}
          >
            Built for Evangelism Leads with complex ministries
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: "rgba(255,255,255,0.82)",
              maxWidth: 760,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            who want to be both <strong>faithful</strong>{" "} and <strong>effective</strong> in bringing the gospel to the nations.
          </Typography>
        </Box>

        <Grid2 container spacing={{ xs: 3, md: 4 }}>
          {audiences.map((a) => (
            <Grid2 key={a.title} size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  height: "100%",
                  borderRadius: 5,
                  p: { xs: 3, md: 4 },
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                  position: "relative",
                  overflow: "hidden",
                  transition: "transform 0.25s ease, border-color 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(255,255,255,0.18)",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: "white", fontWeight: 900, mb: 1.25 }}
                >
                  {a.title}
                </Typography>

                <Typography sx={{ color: "rgba(255,255,255,0.82)", mb: 3 }}>
                  {a.description}
                </Typography>

                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "rgba(255,255,255,0.78)",
                    fontWeight: 800,
                  }}
                >
                  <ArrowForward sx={{ fontSize: 18 }} />
                  <Typography variant="body2">{a.summary}</Typography>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    right: -70,
                    top: -70,
                    width: 190,
                    height: 190,
                    borderRadius: "50%",
                    background: "rgba(77, 222, 128, 0.35)",
                    filter: "blur(10px)",
                  }}
                />
              </Box>
            </Grid2>
          ))}
        </Grid2>

        <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
          <Button
            variant="contained"
            sx={{
              background: "#4ade80",
              color: "#0f172a",
              borderRadius: "9999px",
              px: 4,
              py: 1.5,
              fontSize: "1rem",
              boxShadow: "0px 10px 30px rgba(0,0,0,0.35)",
              "&:hover": { background: "#22c55e" },
              textTransform: "none",
              fontWeight: 900,
              mt: 4,
            }}
            size="large"
            href="/solutions/"
            endIcon={<ArrowForward />}
          >
            See How PATHWAYS Helps Evangelism Leads
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default TheTargetAudience;